import React from 'react';
import './Buttons.css';
import './HelpPage.css';

const HelpPage = () => {
    const [installMsg, setInstallMsg] = React.useState("");
    const [showModal, setShowModal] = React.useState(false);
    const [videoSrc, setVideoSrc] = React.useState(null);

    return (
        <>
            {showModal && (
                <div className="help-modal-overlay">
                    <div className="help-modal-box">
                      {videoSrc ? (
                            <video
                                    autoPlay
                                    controls
                                    controlsList="nodownload"
                                    disablePictureInPicture
                                    style={{
                                        width: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <source src={videoSrc} />
                              </video>

                      ) : (
                            <p style={{ marginBottom: "1rem" }}>
                                This tutorial is currently under development
                            </p>
                      )}

                        <button className="button" onClick={() => setShowModal(false)}>
                            Close
                        </button>
                    </div>
                </div>
            )}

        <div
            className="help-page"
            style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '1.5rem',
                textAlign: 'left'
            }}
        >
            <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>FAQ</h1>

            {/* GENERAL */}
            <section style={{ marginBottom: '2rem' }}>
                <h2>General</h2>

                <details>
                    <summary>Why do I need GrowYourFoodApp?</summary>
                    <p>
                        Gardening is local, timing-sensitive, and changes throughout the year - and most tools only solve one piece of the puzzle.
                        GrowYourFoodApp is your garden companion that brings everything together in one calm, organized place:
                    </p>

                    <ul>
                        <li><strong>The Garden Journal looks back</strong> so you remember what worked, what didn't, and how your garden evolved.</li>
                        <li><strong>The Seasonal Planner looks forward</strong> so you can plan, organize supplies, and anticipate what's next.</li>
                        <li><strong>Weather monitoring is built in</strong> so your tasks adjust to real local conditions.</li>
                        <li><strong>Timely nudges from your garden companion keep you on track</strong> so you never miss a planting window.</li>
                        <li><strong>It remembers your garden year after year</strong>, making each season easier than the last.</li>
                    </ul>
                </details>

                <details>
                    <summary>How does GrowYourFoodApp work?</summary>
                    <p>
                        You choose your location, add the crops you grow, and GrowYourFoodApp, your garden companion, builds a personalized
                        schedule. Tasks arrive on the days they matter, with gentle nudges based on your local
                        climate.
                    </p>
                </details>

                <details>
                        <summary>What if I don't see my location?</summary>
                        <p>
                            GrowYourFoodApp supports specific micro?regions so your planting dates match real local conditions. If you
                            don't see your exact town, simply choose the closest area with a similar climate. Each region is tied to a
                            nearby weather station, and GrowYourFoodApp, your garden companion, pulls updated weather information every
                            night so your schedule stays accurate even if your exact location isn't listed. More regions are being added
                            over time.
                        </p>
                </details>

                <details>
                    <summary>What if I don't know my frost dates?</summary>
                    <p>
                        You don't need to. GrowYourFoodApp, your garden companion already knows your frost dates and climate patterns. 
                        It pulls updated weather information from nearby weather stations every day based on your location. Your planting
                        schedule is built automatically - no charts, research, or guesswork required.
                    </p>
                </details>

                <details>
                    <summary>Do I have to add all my varieties right away?</summary>
                    <p>
                        Not at all. The crop is the important part - that's what builds your schedule. Think of the variety page as a
                        place to store seed?packet details if you have them. If you don't, don't worry. When it's time to plant, the
                        task will simply ask how many varieties you planted and the name of each one. You can add or update variety
                        details anytime.
                    </p>
                </details>

                <details>
                    <summary>What if I grow something that isn't in the app yet?</summary>
                    <p>
                        Choose the closest matching crop for now, or reach out to request a new one. We're always
                        expanding the list based on what real gardeners grow.
                    </p>
                </details>

                <details>
                    <summary>What if I miss a task?</summary>
                    <p>
                        Don't worry - GrowYourFoodApp, your garden companion, keeps tasks stay visible until you answer them, so nothing disappears or gets lost. If you join
                        partway through the year, the app simply plays catch?up by asking what you've already planted so your
                        timeline is complete. Everything that comes later - germinating, thinning, protecting, harvesting - is
                        built off your planting date, so once that's captured, the rest of your schedule stays accurate.
                    </p>
                </details>

                <details>
                    <summary>Can I use GrowYourFoodApp if I'm a total beginner?</summary>
                    <p>
                        Absolutely. GrowYourFoodApp, your garden companion, guides you step by step with clear timing, simple explanations, and gentle
                        nudges. GrowYourFoodApp captures your experience level - beginner, intermediate, or seasoned - so the guidance
                        matches where you're at. And if you want to learn how to grow year-round, you can follow Jordan, our
                        year-round gardener, to see what Jordan is doing in every season and learn at your own pace.
                    </p>
                </details>
            </section>

                {/* INSTALL and NOTIFICATIONS */}
                <section style={{ marginBottom: '2rem' }}>
                    <h2>Install & Notifications</h2>

                    <details>
                        <summary>Do I need to install GrowYourFoodApp on my device?</summary>
                        <p>
                            Installing GrowYourFoodApp puts it on your home screen, gives you a smooth app-like experience,
                            and allows you to receive timely gardening nudges. You can still use it in your browser,
                            but you won't receive notifications unless it's installed and notifications are turned on.
                        </p>
                    </details>

                    <details>
                        <summary>Laptops, computers, and mobile devices?</summary>
                        <p>
                            For the best experience, install GrowYourFoodApp on your <strong>phone or tablet </strong>
                            and turn on notifications. Mobile devices can wake up and alert you about frost or planting times,
                            even when the screen is off - so you never miss an important moment in your garden.
                        </p>
                        <p>
                            If you enjoy a bigger screen for planning or journaling, you can also use GrowYourFoodApp on your laptop
                            or computer. Just keep notifications turned on <strong>only on your phone</strong>, since laptops
                            only receive alerts when they're awake and open.
                        </p>
                        <p>
                            In short: <strong>use your phone for alerts, and your laptop only if you prefer the larger view.</strong>
                        </p>
                    </details>

                    <details>
                        <summary>Why am I not getting seasonal nudges?</summary>
                        <p>Check the following:</p>
                        <ul>
                            <li>GrowYourFoodApp is installed on your device (added to your home screen).</li>
                            <li>You've allowed notifications when prompted.</li>
                            <li>Your device is up to date. iPhones running iOS below 16.4 do not support notifications.</li>
                        </ul>
                    </details>

                    <details>
                        <summary>How do I install GrowYourFoodApp on my device?</summary>
                        <p>
                            Installation steps depend on your device (iPhone, iPad, Android, Samsung). Follow the on-screen
                            prompts when you visit the site, use the install option in your browser menu, or watch the
                            installation tutorial video below.
                        </p>
                    </details>
                    
                    <button
                        className="button"
                        onClick={() => {
                            setVideoSrc("/videos/WatchInstallationTutorial.mp4");
                            setShowModal(true);
                        }}
                    >
                        Watch installation tutorial
                 </button>
                    <button
                        className="button"
                        onClick={() => {
                            setVideoSrc("/videos/AppleInstallation.mp4");
                            setShowModal(true);
                        }}
                    >
                        Watch iPhone/iPad installation tutorial
                 </button>
                    {/*<button className="button" onClick={() => setShowModal(true)}>Watch installation tutorial</button>
                         */}
                </section>

                {/* VARIETIES */}
            <section style={{ marginBottom: '2rem' }}>
                <h2>Varieties</h2>

                <details>
                    <summary>What does "variety" mean?</summary>
                    <p>
                        A variety is the specific type of plant you're growing, such as "Roma tomato" or
                        "Little Gem lettuce." Different varieties can have different timing, frost tolerance,
                        and days to maturity.
                    </p>
                </details>

                <details>
                    <summary>Why do I need to enter all of the variety information?</summary>
                    <p>
                        The more detail you provide, the better your schedule matches reality. This information
                        is usually on the back of seed packets. If you don't have it, general defaults will be used.
                    </p>
                </details>

                <details>
                    <summary>What if I don't know all of a variety's information?</summary>
                    <p>
                        Don't worry. You can usually find it on your seed packet, plant label, or seed catalog.
                        If you don't have all the details, you can still add the variety and update it later.
                    </p>
                    </details>
                    
                    <button
                        className="button"
                        onClick={() => {
                            setVideoSrc("/videos/WatchMyPlantsTutorial.mp4");
                            setShowModal(true);
                        }}
                    >
                        Watch My Plants tutorial
                 </button> 
                    {/*    <button className="button" onClick={() => setShowModal(true)}>Watch My Plants tutorial</button>
                        */}
            </section>

            {/* PLANTING METHODS */}
            <section style={{ marginBottom: '2rem' }}>
                <h2>Planting Methods</h2>

                <details>
                    <summary>What are the various planting methods?</summary>
                    <p>Common methods include:</p>
                    <ul>
                        <li>Direct sowing outdoors</li>
                        <li>Starting seeds indoors</li>
                        <li>Transplanting seedlings</li>
                        <li>Using starter plants from a nursery</li>
                    </ul>
                </details>

                <details>
                    <summary>Why would I start indoors if I can direct sow later?</summary>
                    <p>
                        Starting indoors gives you a head start on the growing season, especially in cooler climates.
                        It often means earlier harvests and stronger plants when they go outside.
                    </p>
                </details>

                <details>
                        <summary>What if I buy starter plants?</summary>
                        <p>
                            No problem. If a crop normally starts indoors, like tomatoes, GrowYourFoodApp will still show you the indoor
                            seeding task. When it asks "Did you plant your tomatoes?" simply choose "no" if you're using starter plants.
                            This tells GrowYourFoodApp that you're skipping the indoor step, and your garden companion will still notify
                            you at the correct time to transplant outdoors. That's the task where you'll record when you put your starter
                            plants into the garden, and all later tasks adjust from that transplanting date.
                        </p>
                </details>
                <button className="button" onClick={() => setShowModal(true)}>Watch planting methods tutorial</button>

                </section>
                <p className="help-footer">
                    GrowYourFoodApp - your garden companion for year-round growing
                </p>

            </div>
        </>
    );
};

export default HelpPage;
